import { useCallback, useMemo } from 'react';
import { useQueries } from '@common-fe/common-fe';
import { FiservCardHolder, FiservCardInterface } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { GET_CARDS_QUERY_PARAMS_KEYS } from '../Cards.constants';
import { CardHoldersResponse, CardsMap, FiservCardPurseDto } from '../Cards.types';
import { useCardFormatting } from '../hooks/useCardFormatting';
import { useQueryGenerator } from '../hooks/useQueryGenerator';

export const QUERY_KEYS = {
  GET_CARDS_HOLDERS: 'GET_CARDS_HOLDERS',
  GET_CARDS_PURSES: 'GET_CARDS_PURSES',
};

export const useGetCardsQuery = (
  employeeId?: number | string | null,
  dependentId?: number | string | null
) => {
  const query = useQueryGenerator(employeeId, dependentId);

  const [cardsQuery, cardPursesQuery] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.GET_CARDS_HOLDERS, query.toString()],
        queryFn: () =>
          query.get(GET_CARDS_QUERY_PARAMS_KEYS.EMPLOYEE_ID)
            ? // TODO: the type is missing
              api.get<CardHoldersResponse>(`${API_URL.CARD_HOLDERS}?${query.toString()}`)
            : null,
      },
      {
        queryKey: [QUERY_KEYS.GET_CARDS_PURSES, employeeId],

        queryFn: () =>
          api.get<FiservCardPurseDto[]>(API_URL.CARD_PURSES, {
            params: {
              employee_id: employeeId,
            },
          }),
      },
    ],
  });

  const {
    data: cardPursesData,
    isLoading: isPursesLoading,
    isFetching: isPursesFetching,
    refetch: refetchCardPurses,
  } = cardPursesQuery;
  const { data, isLoading: isCardsLoading, isFetching: isCardsFetching, refetch: refetchCards } = cardsQuery;

  const isLoading = useMemo(() => isCardsLoading || isPursesLoading, [isCardsLoading, isPursesLoading]);
  const isFetching = useMemo(() => isCardsFetching || isPursesFetching, [isCardsFetching, isPursesFetching]);

  const refetch = useCallback(() => {
    refetchCardPurses();
    refetchCards();
  }, [refetchCardPurses, refetchCards]);

  const { getHolderName, formattingCardsResponse, formattingHolder } = useCardFormatting(
    cardPursesData?.data
  );

  const cardMap: CardsMap = useMemo(() => {
    if (!data?.data) return {};
    const dependents = data.data.dependents || [];
    const rootCardMap: CardsMap = data?.data ? formattingCardsResponse(data?.data) : {};
    const dependentsCardMap: CardsMap = dependents.reduce(
      (map, dependent) => ({
        ...map,
        ...formattingCardsResponse(dependent),
      }),
      {}
    );

    return {
      ...rootCardMap,
      ...dependentsCardMap,
    };
  }, [data, formattingCardsResponse]);

  const holderList: FiservCardHolder[] = useMemo(() => {
    if (!data?.data) return [];

    return data.data.dependents ? data.data.dependents.map(formattingHolder) : [];
  }, [data, formattingHolder]);

  const cardList: FiservCardInterface[] = useMemo(() => {
    return Object.values(cardMap).flatMap((holderCards) => Object.values(holderCards).flat());
  }, [cardMap]);

  const cardAccountId = useMemo(() => cardList[0]?.cardAccountId || null, [cardList]);
  const holderName = getHolderName(data?.data?.first_name, data?.data?.last_name) || '';
  const holderKey = `${holderName}-${data?.data?.id}`;

  // useEffect(() => {
  //   if (query.get(GET_CARDS_QUERY_PARAMS_KEYS.EMPLOYEE_ID)) refetch();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [employeeId]);

  return {
    cardMap,
    response: data?.data,
    holderFirstName: data?.data?.first_name,
    holderName,
    holderKey,
    holderList,
    refetch,
    isLoading,
    isFetching,
    hasCards: !!data?.data,
    cardAccountId,
  };
};
